import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";

function _createSuper(Derived) {
  function isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;

    try {
      Date.prototype.toString.call(Reflect.construct(Date, [], function () {}));
      return true;
    } catch (e) {
      return false;
    }
  }

  return function () {
    var Super = _getPrototypeOf(Derived),
        result;

    if (isNativeReflectConstruct()) {
      var NewTarget = _getPrototypeOf(this).constructor;

      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }

    return _possibleConstructorReturn(this, result);
  };
}

import React, { PureComponent, createElement, cloneElement } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles } from '../theme';
import { isolateMixin, placeholderMixin, ifMobile } from '../utils/mixins';
import { ios, android } from '../utils/browser';
import Tooltip from '../Tooltip';
import ClockIcon from '../icons/forms/ClockIcon';
import CalendarIcon from '../icons/forms/CalendarIcon';
import EyeIcon from '../icons/forms/EyeIcon';
import ClosedEyeIcon from '../icons/forms/ClosedEyeIcon';
var isTouchDevice = ios || android;

var activeBorder = function activeBorder(borderColor) {
  return {
    borderColor: borderColor,
    transform: 'none'
  };
};

var inputColors = function inputColors(background, hoverBackground, borderColor) {
  return {
    background: background,
    borderColor: borderColor,
    '&:enabled:hover': {
      background: hoverBackground || background
    },
    '$icon:hover ~ &': {
      background: hoverBackground || background
    },
    '&:focus': {
      background: background
    }
  };
};

var styles = function styles(theme) {
  return _extends({
    input: _extends({
      extend: isolateMixin,
      fontFamily: theme.field.fontFamily,
      boxSizing: 'border-box',
      display: 'block',
      padding: 0,
      outline: 0,
      width: '100%',
      fontStyle: theme.field.fontStyle,
      fontWeight: theme.field.fontWeight,
      letterSpacing: theme.field.letterSpacing,
      appearance: 'none',
      lineHeight: 'normal',
      color: theme.field.colors.default.text,
      boxShadow: 'none',
      border: '0 solid',
      transition: "all ".concat(theme.field.animationDuration, "ms ease")
    }, ifMobile({
      fontWeight: theme.field.mobile.fontWeight,
      letterSpacing: theme.field.mobile.letterSpacing
    }), {}, inputColors(theme.field.colors.default.background, theme.field.colors.hover.background, theme.field.colors.default.outline), {
      '$textareaRoot &': {
        resize: 'vertical',
        height: '100%'
      },
      '&::-ms-reveal, &::-ms-clear': {
        display: 'none'
      },
      '&[type="month"], &[type="date"], &[type="time"]': {
        '&::-webkit-clear-button, &::-webkit-inner-spin-button': {
          display: 'none'
        },
        '&::-webkit-date-and-time-value': {
          margin: 0,
          textAlign: 'left'
        }
      },
      '&::-webkit-calendar-picker-indicator': {
        display: 'none',
        '$withRightIcon &': {
          display: 'block',
          position: 'absolute',
          margin: 0,
          padding: 0,
          background: 'transparent'
        }
      },
      '&:enabled:hover, $icon:hover ~ &:enabled': {
        borderColor: theme.field.colors.hover.outline
      },
      '&:disabled': {
        background: theme.field.colors.disabled.background,
        color: theme.field.colors.disabled.text,
        cursor: 'not-allowed',
        borderColor: theme.field.colors.disabled.outline
      },
      '&$filled[type="password"]': {
        fontFamily: 'monospace'
      },
      '&:focus + $activeBorder + $placeholder': {
        display: 'none'
      }
    }, placeholderMixin('&', {
      color: theme.field.colors.default.placeholder,
      opacity: 1,
      transition: "opacity ".concat(Math.round(theme.field.animationDuration * 0.7), "ms linear")
    }), {}, placeholderMixin('&:disabled', {
      color: theme.field.colors.disabled.placeholder,
      opacity: 1
    }), {}, placeholderMixin('&:focus', {
      opacity: 0.54
    }), {}, placeholderMixin('$isEnabled$isFocused &', {
      opacity: 0.54
    }), {
      '$error$isEnabled &': inputColors(theme.field.colors.error.background, theme.field.colors.errorHover.background, theme.field.colors.error.outline),
      '$warning$isEnabled &': inputColors(theme.field.colors.warning.background, theme.field.colors.warningHover.background, theme.field.colors.warning.outline),
      '$success$isEnabled &': inputColors(theme.field.colors.success.background, theme.field.colors.successHover.background, theme.field.colors.success.outline)
    }),
    withStatusLine: {
      '& $activeBorder': {
        borderWidth: [0, 0, 2],
        transform: 'scaleX(0.6) scaleY(0)',
        transformOrigin: 'center bottom'
      }
    },
    withOutline: {
      '& $input': {
        borderRadius: theme.field.borderRadius,
        borderWidth: 1
      }
    },
    withCounter: {},
    regular: {
      composes: ['$withOutline'],
      '& $activeBorder': {
        borderRadius: theme.field.borderRadius,
        borderWidth: 1
      }
    },
    awesome: {
      composes: ['$withOutline', '$withStatusLine'],
      '& $activeBorder': {
        borderRadius: theme.field.borderRadius
      }
    },
    promo: {
      composes: ['$withStatusLine'],
      '& $input': {
        borderWidth: [1, 0],
        borderTopColor: 'transparent!important'
      },
      '& $input, & $input:disabled': {
        background: 'none'
      },
      '& $activeBorder': {
        borderRadius: theme.field.borderRadius
      }
    }
  }, ['medium', 'small'].reduce(function (result, size) {
    return _extends({}, result, _defineProperty({}, size, _extends({
      '& $input': _extends({
        fontSize: theme.field.sizes[size].fontSize
      }, ifMobile({
        fontSize: theme.field.mobile.sizes[size].fontSize
      })),
      '& input$input': _extends({
        height: theme.field.sizes[size].height
      }, ifMobile({
        height: theme.field.mobile.sizes[size].height
      }), {
        '&[type="month"], &[type="date"], &[type="time"]': _extends({}, ifMobile({
          lineHeight: theme.field.sizes[size].height + 'px'
        }))
      }),
      '& $characterCounter': {
        lineHeight: theme.field.sizes[size].height + 'px'
      },
      '&$textareaRoot $input': _extends({
        paddingTop: theme.input.sizes[size].verticalPadding,
        paddingBottom: theme.input.sizes[size].verticalPadding
      }, ifMobile({
        paddingTop: theme.input.mobile.sizes[size].verticalPadding,
        paddingBottom: theme.input.mobile.sizes[size].verticalPadding
      }))
    }, ['& $icon', '& $input::-webkit-calendar-picker-indicator'].reduce(function (result, selector) {
      return _extends({}, result, _defineProperty({}, selector, _extends({
        height: theme.field.sizes[size].icon,
        width: theme.field.sizes[size].icon,
        lineHeight: theme.field.sizes[size].icon + 'px'
      }, ifMobile({
        height: theme.field.mobile.sizes[size].icon,
        width: theme.field.mobile.sizes[size].icon,
        lineHeight: theme.field.mobile.sizes[size].icon + 'px'
      }))));
    }, {}), {
      '& $eye': _extends({
        height: theme.field.sizes[size].eyeIcon,
        width: theme.field.sizes[size].eyeIcon,
        lineHeight: theme.field.sizes[size].eyeIcon + 'px'
      }, ifMobile({
        height: theme.field.mobile.sizes[size].eyeIcon,
        width: theme.field.mobile.sizes[size].eyeIcon,
        lineHeight: theme.field.mobile.sizes[size].eyeIcon + 'px'
      })),
      '& $eyeWrapper': {
        '&:after': _extends({
          display: 'block',
          content: '" "',
          position: 'absolute',
          top: -Math.floor((theme.field.sizes[size].height - theme.field.sizes[size].eyeIcon) / 2),
          bottom: -Math.floor((theme.field.sizes[size].height - theme.field.sizes[size].eyeIcon) / 2),
          left: -10,
          right: -10
        }, ifMobile({
          top: -Math.floor((theme.field.sizes[size].height - theme.field.mobile.sizes[size].eyeIcon) / 2),
          bottom: -Math.floor((theme.field.sizes[size].height - theme.field.mobile.sizes[size].eyeIcon) / 2)
        }), {
          borderRadius: theme.field.borderRadius
        })
      },
      '& $placeholder': _extends({
        fontSize: theme.field.sizes[size].fontSize,
        pointerEvents: 'none'
      }, ifMobile({
        fontSize: theme.field.mobile.sizes[size].fontSize
      })),
      '&$withOutline $placeholder': _extends({
        paddingLeft: theme.input.sizes[size].padding
      }, ifMobile({
        paddingLeft: theme.input.mobile.sizes[size].padding
      })),
      '&$withOutline $input': _extends({
        paddingLeft: theme.input.sizes[size].padding,
        paddingRight: theme.input.sizes[size].padding
      }, ifMobile({
        paddingLeft: theme.input.mobile.sizes[size].padding,
        paddingRight: theme.input.mobile.sizes[size].padding
      })),
      '&$withLeftIcon$regular $input': _extends({
        paddingLeft: theme.field.sizes[size].withIconPadding - 1
      }, ifMobile({
        paddingLeft: theme.field.mobile.sizes[size].withIconPadding - 1
      })),
      '&$withLeftIcon$awesome $input': _extends({
        paddingLeft: theme.field.sizes[size].withIconPadding
      }, ifMobile({
        paddingLeft: theme.field.mobile.sizes[size].withIconPadding
      })),
      '&$withLeftIcon$promo $input': _extends({
        paddingLeft: theme.field.sizes[size].withIconPadding - theme.input.sizes[size].padding
      }, ifMobile({
        paddingLeft: theme.field.mobile.sizes[size].withIconPadding - theme.input.mobile.sizes[size].padding
      })),
      '&$withRightIcon$regular $input, &$withEye$regular $input': _extends({
        paddingRight: theme.field.sizes[size].withIconPadding - 1
      }, ifMobile({
        paddingRight: theme.field.mobile.sizes[size].withIconPadding - 1
      })),
      '&$withEye$withRightIcon$regular $input': _extends({
        paddingRight: theme.field.sizes[size].withIconsPadding - 1
      }, ifMobile({
        paddingRight: theme.field.mobile.sizes[size].withIconsPadding - 1
      })),
      '&$withRightIcon$awesome $input, &$withEye$awesome $input': _extends({
        paddingRight: theme.field.sizes[size].withIconPadding
      }, ifMobile({
        paddingRight: theme.field.mobile.sizes[size].withIconPadding
      })),
      '&$withEye$withRightIcon$awesome $input': _extends({
        paddingRight: theme.field.sizes[size].withIconsPadding
      }, ifMobile({
        paddingRight: theme.field.mobile.sizes[size].withIconsPadding
      })),
      '&$withRightIcon$promo $input, &$withEye$promo $input': _extends({
        paddingRight: theme.field.sizes[size].withIconPadding - theme.input.sizes[size].padding
      }, ifMobile({
        paddingRight: theme.field.mobile.sizes[size].withIconPadding - theme.input.mobile.sizes[size].padding
      })),
      '&$withEye$withRightIcon$promo $input': _extends({
        paddingRight: theme.field.sizes[size].withIconsPadding - theme.input.sizes[size].padding
      }, ifMobile({
        paddingRight: theme.field.mobile.sizes[size].withIconsPadding - theme.input.mobile.sizes[size].padding
      })),
      '&$withEye$regular $iconRight, &$withEye$awesome $iconRight': _extends({
        right: theme.field.sizes[size].withIconPadding
      }, ifMobile({
        right: theme.field.mobile.sizes[size].withIconPadding
      })),
      '&$withEye$promo $iconRight': _extends({
        right: theme.field.sizes[size].withIconPadding - theme.input.sizes[size].padding
      }, ifMobile({
        right: theme.field.mobile.sizes[size].withIconPadding - theme.input.mobile.sizes[size].padding
      })),
      '&$withCounter $input': _extends({
        paddingRight: theme.field.sizes[size].withIconPadding - 1
      }, ifMobile({
        paddingRight: theme.field.mobile.sizes[size].withIconPadding - 1
      })),
      '&$regular $iconLeft, &$awesome $iconLeft': _extends({
        left: theme.field.sizes[size].iconMargin
      }, ifMobile({
        left: theme.field.mobile.sizes[size].iconMargin
      })),
      '&$promo $iconLeft': {
        left: 0
      },
      '&$regular, &$awesome': ['& $iconRight', '& $input::-webkit-calendar-picker-indicator'].reduce(function (result, selector) {
        return _extends({}, result, _defineProperty({}, selector, _extends({
          right: theme.field.sizes[size].iconMargin
        }, ifMobile({
          right: theme.field.mobile.sizes[size].iconMargin
        }))));
      }, {}),
      '&$promo': ['& $iconRight', '& $input::-webkit-calendar-picker-indicator'].reduce(function (result, selector) {
        return _extends({}, result, _defineProperty({}, selector, {
          right: 0
        }));
      }, {}),
      '&$inGroup$regular, &$inGroup$awesome': {
        '&:not($startPosition)': {
          '& $input, & $activeBorder': {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0
          }
        },
        '&:not($endPosition)': {
          '& $input, & $activeBorder': {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0
          }
        }
      },
      '&$inGroup$promo': {
        '&:not($startPosition)': {
          '&$withLeftIcon $input': _extends({
            paddingLeft: theme.field.sizes[size].withIconPadding
          }, ifMobile({
            paddingLeft: theme.field.mobile.sizes[size].withIconPadding
          })),
          '& $iconLeft': _extends({
            left: theme.field.sizes[size].iconMargin
          }, ifMobile({
            left: theme.field.mobile.sizes[size].iconMargin
          }))
        },
        '&:not($endPosition)': {
          '&$withRightIcon $input, &$withEye $input': _extends({
            paddingRight: theme.field.sizes[size].withIconPadding
          }, ifMobile({
            paddingRight: theme.field.mobile.sizes[size].withIconPadding
          })),
          '&$withEye$withRightIcon $input': _extends({
            paddingRight: theme.field.sizes[size].withIconsPadding - 1
          }, ifMobile({
            paddingRight: theme.field.mobile.sizes[size].withIconsPadding - 1
          })),
          '&$withEye $iconRight': _extends({
            right: theme.field.sizes[size].withIconPadding
          }, ifMobile({
            right: theme.field.mobile.sizes[size].withIconPadding
          })),
          '& $iconRight': _extends({
            right: theme.field.sizes[size].iconMargin
          }, ifMobile({
            right: theme.field.mobile.sizes[size].iconMargin
          }))
        }
      }
    })));
  }, {}), {
    root: {
      extend: isolateMixin,
      position: 'relative',
      boxSizing: 'border-box',
      fontFamily: theme.fontFamily
    },
    textareaRoot: {},
    characterCounter: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 15,
      margin: 'auto',
      fontSize: theme.input.counter.fontSize,
      fontWeight: theme.input.counter.fontWeight,
      color: theme.input.counter.colors.default,
      '$textareaRoot &': {
        top: 'auto',
        bottom: 10,
        lineHeight: 1.36
      }
    },
    characterCounterWarn: {
      color: '#ffc000'
    },
    characterCounterError: {
      color: '#ff564e'
    },
    activeBorder: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none',
      transition: "all ".concat(Math.round(theme.field.animationDuration * 0.7), "ms linear"),
      border: '0 solid transparent',
      '$input:focus + &, $isEnabled$isFocused &': activeBorder(theme.field.colors.focus.border),
      '$success$isEnabled &': activeBorder(theme.field.colors.success.border || theme.colors.success),
      '$error$isEnabled &': activeBorder(theme.field.colors.error.border || theme.colors.danger),
      '$warning$isEnabled &': activeBorder(theme.field.colors.warning.border || theme.colors.warn)
    },
    icon: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      margin: 'auto',
      fontSize: 0,
      color: theme.field.icon.colors.default,
      '$textareaRoot &': _extends({
        marginTop: Math.ceil((theme.field.sizes.medium.height - theme.field.sizes.medium.icon) / 2)
      }, ifMobile({
        marginTop: Math.ceil((theme.field.mobile.sizes.medium.height - theme.field.mobile.sizes.medium.icon) / 2)
      }))
    },
    eye: {
      composes: '$icon',
      pointerEvents: 'auto',
      border: 0,
      outline: 0,
      cursor: 'pointer',
      '&:hover': {
        color: theme.field.icon.colors.hover
      },
      '&:active': {
        color: theme.field.icon.colors.active
      },
      '$regular &, $awesome &': _extends({
        right: theme.input.eyeMargin
      }, ifMobile({
        right: theme.input.mobile.eyeMargin
      })),
      '$promo &': {
        right: 0
      },
      '$isDisabled &': {
        cursor: 'not-allowed',
        color: theme.field.colors.disabled.text,
        pointerEvents: 'none'
      }
    },
    withLeftIcon: {},
    withRightIcon: {},
    withEye: {},
    iconLeft: {
      composes: '$icon',
      pointerEvents: 'none'
    },
    iconRight: {
      composes: '$icon',
      pointerEvents: 'none'
    },
    placeholder: {
      position: 'absolute',
      top: '2px',
      left: '1px',
      height: 'calc(100% - 5px)',
      display: 'flex',
      alignItems: 'center',
      background: '#fff',
      color: theme.field.colors.default.placeholder,
      opacity: 1,
      transition: "opacity ".concat(Math.round(theme.field.animationDuration * 0.7), "ms linear")
    },
    isFocused: {},
    filled: {},
    isDisabled: {},
    isEnabled: {},
    inGroup: {},
    success: {},
    error: {},
    warning: {},
    eyeWrapper: {},
    endPosition: {},
    startPosition: {},
    middlePosition: {}
  });
};

var _ref = /*#__PURE__*/React.createElement(ClockIcon, null);

var _ref2 = /*#__PURE__*/React.createElement(CalendarIcon, null);

var Input = /*#__PURE__*/function (_PureComponent) {
  _inherits(Input, _PureComponent);

  var _super = _createSuper(Input);

  function Input() {
    var _this;

    _classCallCheck(this, Input);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));
    _this.state = {
      type: _this.props.type
    };

    _this.saveRef = function (ref) {
      _this.input = ref;
      if (_this.props.inputRef) _this.props.inputRef(ref);
    };

    _this.inputTypeHelper = function () {
      _this.input.type = _this.state.type === 'password' ? 'text' : 'password';

      _this.setState({
        type: _this.input.type
      });
    };

    _this.onChange = function (event) {
      var onChange = _this.props.onChange;
      if (onChange) onChange(event, event.target.value);
    };

    return _this;
  }

  _createClass(Input, [{
    key: "renderPasswordIcon",
    value: function renderPasswordIcon() {
      var _this$props = this.props,
          type = _this$props.type,
          theme = _this$props.theme,
          size = _this$props.size,
          classes = _this$props.classes,
          passwordIconTooltip = _this$props.passwordIconTooltip,
          passwordIconProps = _this$props.passwordIconProps;
      if (type !== 'password') return null;
      var currentType = this.state.type;
      var Icon = currentType === 'password' ? ClosedEyeIcon : EyeIcon;
      var iconProps = typeof passwordIconProps === 'function' ? passwordIconProps(currentType) : passwordIconProps;
      var icon = React.createElement("span", _extends({}, iconProps, {
        className: classes.eyeWrapper,
        onClick: this.inputTypeHelper
      }), React.createElement(Icon, {
        size: isTouchDevice ? theme.field.mobile.sizes[size].eyeIcon : theme.field.sizes[size].eyeIcon,
        className: classes.eyeIcon,
        color: "currentColor"
      }));

      if (passwordIconTooltip) {
        var content = typeof passwordIconTooltip === 'function' ? passwordIconTooltip(currentType) : passwordIconTooltip;
        return React.createElement(Tooltip, _extends({
          content: content,
          className: classes.eye,
          position: "top"
        }, isTouchDevice && {
          positionX: 'right'
        }), icon);
      }

      return React.createElement("div", {
        className: classes.eye
      }, icon);
    }
  }, {
    key: "renderIcon",
    value: function renderIcon(Icon, className) {
      var props = this.props;
      var field = props.theme.field;
      return React.createElement("div", {
        className: className
      }, cloneElement(Icon, {
        color: props.disabled ? field.colors.disabled.text : Icon.props.color || 'currentColor',
        size: Icon.props.size || (isTouchDevice ? field.mobile.sizes[props.size].icon : field.sizes[props.size].icon)
      }));
    }
  }, {
    key: "renderPlaceholder",
    value: function renderPlaceholder() {
      var _this$props2 = this.props,
          type = _this$props2.type,
          value = _this$props2.value,
          placeholder = _this$props2.placeholder,
          classes = _this$props2.classes;
      if ((type === 'month' || type === 'date' || type === 'time') && (value === '' || value == null) && placeholder) return React.createElement("span", {
        className: classes.placeholder
      }, placeholder);
      return null;
    }
  }, {
    key: "renderCharacterCounter",
    value: function renderCharacterCounter() {
      var _this$props3 = this.props,
          maxLength = _this$props3.maxLength,
          value = _this$props3.value,
          classes = _this$props3.classes;
      var length = value ? value.length : 0;
      var statusClassName = length >= maxLength ? classes.characterCounterError : length >= Math.ceil(maxLength * 0.95) // eslint-disable-next-line indent
      ? classes.characterCounterWarn // eslint-disable-next-line indent
      : '';
      return React.createElement("span", {
        className: "".concat(classes.characterCounter, " ").concat(statusClassName)
      }, maxLength - length);
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props4 = this.props,
          _this$props4$tag = _this$props4.tag,
          tag = _this$props4$tag === void 0 ? 'input' : _this$props4$tag,
          className = _this$props4.className,
          style = _this$props4.style,
          disabled = _this$props4.disabled,
          inputStyle = _this$props4.inputStyle,
          inputClassName = _this$props4.inputClassName,
          iconLeftClassName = _this$props4.iconLeftClassName,
          iconRightClassName = _this$props4.iconRightClassName,
          activeBorderClassName = _this$props4.activeBorderClassName,
          size = _this$props4.size,
          variation = _this$props4.variation,
          iconLeft = _this$props4.iconLeft,
          iconRight = _this$props4.iconRight,
          status = _this$props4.status,
          isFocused = _this$props4.isFocused,
          classes = _this$props4.classes,
          value = _this$props4.value,
          groupPosition = _this$props4.groupPosition,
          theme = _this$props4.theme,
          onChange = _this$props4.onChange,
          passwordIconTooltip = _this$props4.passwordIconTooltip,
          passwordIconProps = _this$props4.passwordIconProps,
          inputRef = _this$props4.inputRef,
          maxLength = _this$props4.maxLength,
          characterCounter = _this$props4.characterCounter,
          containerRef = _this$props4.containerRef,
          other = _objectWithoutProperties(_this$props4, ["tag", "className", "style", "disabled", "inputStyle", "inputClassName", "iconLeftClassName", "iconRightClassName", "activeBorderClassName", "size", "variation", "iconLeft", "iconRight", "status", "isFocused", "classes", "value", "groupPosition", "theme", "onChange", "passwordIconTooltip", "passwordIconProps", "inputRef", "maxLength", "characterCounter", "containerRef"]);

      var type = this.props.type;
      var resultClassName = classnames(className, classes.root, classes[variation], classes[status], disabled ? classes.isDisabled : classes.isEnabled, classes[size], groupPosition && [classes["".concat(groupPosition, "Position")], classes.inGroup], isFocused && classes.isFocused, iconLeft && classes.withLeftIcon, this.iconRight && classes.withRightIcon, characterCounter && classes.withCounter, tag === 'textarea' && classes.textareaRoot, type === 'password' && classes.withEye);
      var inputElement = createElement(tag, _extends({
        value: value,
        disabled: disabled,
        ref: this.saveRef,
        className: classnames(classes.input, inputClassName, value !== '' && value != null && classes.filled),
        style: inputStyle,
        onChange: this.onChange,
        tabIndex: 0
      }, !!maxLength && {
        maxLength: characterCounter ? Math.ceil(maxLength * 1.05) : maxLength
      }, {}, other));
      return React.createElement("div", {
        style: style,
        className: resultClassName,
        ref: containerRef
      }, iconLeft && this.renderIcon(iconLeft, classnames(iconLeftClassName, classes.iconLeft)), this.iconRight && this.renderIcon(this.iconRight, classnames(iconRightClassName, classes.iconRight)), this.renderPasswordIcon(), inputElement, React.createElement("div", {
        className: classnames(activeBorderClassName, classes.activeBorder)
      }), this.renderPlaceholder(), characterCounter && this.renderCharacterCounter());
    }
  }, {
    key: "iconRight",
    get: function get() {
      var _this$props5 = this.props,
          type = _this$props5.type,
          iconRight = _this$props5.iconRight;
      if (iconRight !== undefined) return iconRight;
      if (type === 'time') return _ref;
      if (type === 'date' || type === 'month') return _ref2;
      return null;
    }
  }]);

  return Input;
}(PureComponent);

Input.defaultProps = {
  status: null,
  size: 'medium',
  variation: 'awesome'
};
Input.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   *  Значение введённое в поле, возвращается в callback onChange.
   *  Можно задать дефолтное значение.
   */
  value: PropTypes.any.isRequired,

  /**
   *  Значение placeholder для input
   */
  placeholder: PropTypes.string,

  /**
   * Задизэйблить input true или false
   */
  disabled: PropTypes.bool,

  /**
   * Тип поля (на данный момент, cо временем добавим другие типы полей).
   */
  type: PropTypes.oneOf(['email', 'number', 'password', 'tel', 'text', 'url', 'time', 'date', 'month']),

  /**
   * Размер инпута
   */
  size: PropTypes.oneOf(['small', 'medium']),

  /**
   * Разновидность инпута
   */
  variation: PropTypes.oneOf(['regular', 'awesome', 'promo']),

  /**
   * Валидация input'a
   */
  status: PropTypes.oneOf(['error', 'warning', 'success', 'filled', null]),

  /**
   * Подсветка импута, как в состоянии `:focus`
   */
  isFocused: PropTypes.bool,

  /**
   * Класс контейнера
   */
  className: PropTypes.string,

  /**
   * Класс элемента input
   */
  inputClassName: PropTypes.string,

  /**
   * По умолчанию элемент input растягивается на всю ширину родительского контейнера.
   * Т.е. задавать ширину через родительский контейнер - объект style.
   * Сюда не стоит передавать какое-либо значение.
   */
  fullWidth: PropTypes.any,

  /**
   * Переопределение стандартных стилей input
   */
  inputStyle: PropTypes.object,

  /**
   * Переопределение стилей контейнера для input
   */
  style: PropTypes.object,

  /**
   * Callback onChange возвращает event и event.target.value
   */
  onChange: PropTypes.func,

  /**
   *  icon слева
   */
  iconLeft: PropTypes.node,

  /**
   *  icon справа
   */
  iconRight: PropTypes.node,

  /**
   * Текст подсказки для кнопки смены статуса типа password, ожидается `String`
   * или функция возвращающая `String`: currentType => 'Показать пароль'
   */
  passwordIconTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * Дополнительные атрибуты кнопки смены статуса типа password
   */
  passwordIconProps: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),

  /**
   * Позиция инпута в группе
   */
  groupPosition: PropTypes.oneOf(['start', 'middle', 'end', null]),

  /**
   * Дополнительный класс правой иконки
   */
  iconRightClassName: PropTypes.string,

  /**
   * Дополнительный класс левой иконки
   */
  iconLeftClassName: PropTypes.string,

  /**
   * Дополнительный класс границы
   */
  activeBorderClassName: PropTypes.string,

  /**
   * Отображения счетчика символов
   */
  characterCounter: PropTypes.bool,

  /**
   * Коллбек для передачи ссылки на ноду корневого элемента
   */
  containerRef: PropTypes.func,

  /**
   * Коллбек для передачи ссылки на ноду элемента input
   */
  inputRef: PropTypes.func
} : {};
export default withStyles(styles, {
  name: 'Input'
})(Input);